// src/pages/student/ExtensionRequestWizard.jsx
// Dedicated wizard for students to request an extension for a past or
// exam-locked item, without having to find the right card in the lists.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  FiArrowLeft, FiArrowRight, FiCheckCircle, FiClock, FiFileText,
  FiSearch, FiInbox, FiAlertCircle, FiUser,
} from 'react-icons/fi';
import { TbCalendarOff, TbChecklist } from 'react-icons/tb';
import apiService from '../../services/api';
import logger from '../../utils/logger';

const MIN_LEN = 20;

/* -------------------------- Layout -------------------------- */
const Page = styled.div`
  position: relative;
  padding: 2.5rem 2rem 4rem;
  max-width: 1160px;
  margin: 0 auto;
  font-family: 'Inter', system-ui, sans-serif;
  box-sizing: border-box;
  overflow-x: hidden;
  @media (max-width: 768px) { padding: 1.5rem 1rem 3rem; }
  @media (max-width: 420px) { padding: 1.125rem 0.75rem 2.5rem; }
`;

const PageOrb = styled.div`
  display: none;
`;

const PageInner = styled.div`
  position: relative;
  z-index: 1;
`;

const Hero = styled.header` margin-bottom: 1.75rem; `;
const HeroEyebrow = styled.div`
  font-size: 0.75rem; font-weight: 500; letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--text-muted); margin-bottom: 0.5rem;
`;
const HeroTitleWrap = styled.div`
  position: relative; display: inline-block;
  &::after {
    content: ''; position: absolute; left: 0; bottom: 0;
    width: 2.25rem; height: 2px; border-radius: 2px;
    background: #B8956A;
  }
`;
const HeroTitle = styled.h1`
  font-size: clamp(1.625rem, 2.4vw + 1rem, 2.25rem);
  font-weight: 600; letter-spacing: -0.03em; line-height: 1.1;
  margin: 0 0 0.625rem 0;
  color: var(--text-primary, #1a202c);
  display: inline-block; padding-bottom: 0.25em;
`;
const HeroSub = styled.p`
  font-size: 0.9375rem; color: var(--text-muted); margin: 0; line-height: 1.55;
  max-width: 56ch;
`;

const ContentShell = styled.div`
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SectionNav = styled.aside`
  background: var(--background-primary, #fff);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.035);
  position: sticky;
  top: 1rem;

  @media (max-width: 860px) {
    position: static;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }

  @media (max-width: 420px) { grid-template-columns: 1fr; }
`;

const SectionNavItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 0;
  border-left: ${(p) => (p.$active ? '2px solid #B8956A' : '2px solid transparent')};
  background: ${(p) => (p.$active ? 'rgba(184, 149, 106, 0.08)' : 'transparent')};
  color: ${(p) => (p.$active ? '#8f6b3f' : 'var(--text-muted, #4a5568)')};
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: ${(p) => (p.$active ? 650 : 500)};
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(184, 149, 106, 0.07);
    color: #8f6b3f;
  }
`;

const MainStack = styled.div`
  min-width: 0;
`;

const PanelCard = styled.section`
  background: var(--background-primary, #fff);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.045);
  padding: 1.75rem;

  @media (max-width: 600px) { padding: 1.25rem; }
  @media (max-width: 420px) { padding: 1rem; }
`;

const DesktopLabel = styled.span`
  @media (max-width: 520px) { display: none; }
`;

/* Step indicator — refined horizontal track with progress fill */
const StepsTrack = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(${(p) => p.$count}, 1fr);
  gap: 0;
  margin: 0 0 2rem 0;
  padding: 0;

  @media (max-width: 520px) {
    margin-bottom: 1.25rem;
  }
`;

const MobileStepText = styled.div`
  display: none;
  margin: -0.5rem 0 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b4f25;
  text-align: center;

  @media (max-width: 380px) { display: block; }
`;

const StepCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const StepNode = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(p) => (p.$active ? '#B8956A' : p.$done ? '#5C8676' : '#fff')};
  color: ${(p) => (p.$active || p.$done ? '#fff' : 'var(--text-muted)')};
  border: 2px solid ${(p) => (p.$active ? '#B8956A' : p.$done ? '#5C8676' : 'rgba(0,0,0,0.10)')};
  box-shadow: ${(p) => (p.$active ? '0 0 0 4px rgba(184, 149, 106, 0.18)' : 'none')};
  font-size: 13px;
  font-weight: 600;
  transition: all 0.25s ease;
  z-index: 1;

  @media (max-width: 420px) {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
`;

const StepLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(p) => (p.$active ? '#6b4f25' : p.$done ? '#466B5E' : 'var(--text-muted)')};
  margin-top: 8px;
  white-space: nowrap;

  @media (max-width: 520px) {
    font-size: 0.625rem;
    letter-spacing: 0.02em;
  }

  @media (max-width: 380px) {
    display: none;
  }
`;

const StepLine = styled.div`
  position: absolute;
  top: 16px;
  left: ${(p) => (p.$position === 'left' ? '0' : '50%')};
  right: ${(p) => (p.$position === 'right' ? '0' : '50%')};
  height: 2px;
  background: ${(p) => (p.$done ? '#5C8676' : 'rgba(0,0,0,0.08)')};
  z-index: 0;
  transition: background 0.3s ease;

  @media (max-width: 420px) { top: 14px; }
`;

/* Tile / button card primitives */
const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols || 2}, 1fr);
  gap: 0.75rem;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;
const Tile = styled(motion.button)`
  position: relative;
  display: flex; flex-direction: column; align-items: flex-start; gap: 12px;
  padding: 1.375rem 1.5rem;
  border-radius: 16px;
  background: ${(p) => (p.$selected ? 'rgba(184, 149, 106, 0.06)' : '#fff')};
  border: 1.5px solid ${(p) => (p.$selected ? 'rgba(184, 149, 106, 0.60)' : 'rgba(0, 0, 0, 0.07)')};
  box-shadow: ${(p) =>
    p.$selected
      ? '0 0 0 1px rgba(184, 149, 106, 0.50), 0 14px 32px rgba(184, 149, 106, 0.12)'
      : '0 1px 2px rgba(0, 0, 0, 0.025)'};
  cursor: pointer; text-align: left; font-family: inherit;
  overflow: hidden;
  transition:
    background 0.22s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;

  &:hover:not(:disabled) {
    border-color: rgba(184, 149, 106, 0.55);
    box-shadow: 0 14px 32px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  &:disabled { opacity: 0.5; cursor: not-allowed; }

  @media (max-width: 600px) {
    padding: 1rem;
    border-radius: 14px;
    gap: 10px;
  }
`;
const TileIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 46px; height: 46px;
  border-radius: 12px;
  background: ${(p) => p.$tint || 'rgba(184, 149, 106, 0.14)'};
  color: ${(p) => p.$color || '#6b4f25'};
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);

  @media (max-width: 600px) {
    width: 40px;
    height: 40px;
    border-radius: 10px;
  }
`;

const TileTitle = styled.div`
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--text-primary, #1a202c);
  letter-spacing: -0.015em;
  line-height: 1.25;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media (max-width: 600px) { font-size: 1rem; }
`;

const TileSub = styled.div`
  font-size: 0.875rem;
  color: var(--text-muted, #4a5568);
  line-height: 1.5;
  overflow-wrap: anywhere;
`;

const TileBadge = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 10px; border-radius: 999px;
  font-size: 11px; font-weight: 500;
  background: rgba(184, 149, 106, 0.10);
  color: #6b4f25;
  border: 1px solid rgba(184, 149, 106, 0.20);
  margin-top: 4px;
  max-width: 100%;
  white-space: normal;
`;

const TileInstructor = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-muted, #4a5568);
  max-width: 100%;
  overflow-wrap: anywhere;
`;

const TileDateLine = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-light, #718096);
  max-width: 100%;
  overflow-wrap: anywhere;
`;

/* Section heading + search bar */
const SectionRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; margin: 1.25rem 0 0.75rem;

  @media (max-width: 520px) {
    align-items: flex-start;
  }
`;
const SectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: var(--text-muted);

  &::before {
    content: '';
    display: inline-block;
    width: 18px;
    height: 1px;
    background: rgba(184, 149, 106, 0.45);
  }
`;
const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  max-width: 280px;
  margin-bottom: 1rem;

  @media (max-width: 600px) { max-width: none; width: 100%; }
`;
const SearchInput = styled.input`
  width: 100%; height: 34px; padding: 0 12px 0 32px;
  border-radius: 8px; border: 1px solid rgba(0, 0, 0, 0.12);
  font-family: inherit; font-size: 13px;
  &:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(184,149,105,0.14); }
  @media (max-width: 600px) { height: 40px; font-size: 14px; }
`;
const SearchIcon = styled.span`
  position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  color: var(--text-muted); display: inline-flex;
`;

/* Footer / actions */
const Footer = styled.footer`
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);

  @media (max-width: 520px) {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const FooterSpacer = styled.span`
  @media (max-width: 520px) { display: none; }
`;
const Btn = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  padding: 10px 20px; border-radius: 10px;
  font-family: inherit; font-size: 0.875rem; font-weight: 500;
  letter-spacing: 0.005em;
  border: 1px solid transparent; cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  @media (max-width: 520px) { width: 100%; }
`;
const BackBtn = styled(Btn)`
  background: transparent; color: var(--text-primary); border-color: rgba(0, 0, 0, 0.12);
  &:hover:not(:disabled) { background: rgba(0, 0, 0, 0.03); border-color: rgba(0, 0, 0, 0.22); }
`;
const NextBtn = styled(Btn)`
  background: #B8956A;
  color: #fff;
  min-width: 152px;
  box-shadow: 0 2px 6px rgba(184, 149, 106, 0.30);
  &:hover:not(:disabled) {
    background: #9A7A4F;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(184, 149, 106, 0.35);
  }
`;

/* Step body container */
const StepBody = styled(motion.div)`
  position: relative;
  padding: 0;
  overflow: hidden;

  /* Hairline gold accent along the top edge */
  &::before {
    content: none;
  }

  @media (max-width: 600px) {
    padding: 0;
  }

  @media (max-width: 420px) {
    padding: 0;
  }
`;

const StepTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 1rem;
`;

const RecentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.625rem;

  @media (max-width: 360px) { grid-template-columns: 1fr; }
`;

const RecentCard = styled.div`
  position: relative;
  display: flex; flex-direction: column; gap: 6px;
  padding: 12px 14px;
  background: var(--background-primary, #fff);
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-left: 3px solid ${(p) => p.$accent || 'rgba(0,0,0,0.10)'};
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.025);
  overflow: hidden;
`;

const RecentTopRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px;

  @media (max-width: 420px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const RecentBody = styled.div`
  min-width: 0;
`;

const RecentItemTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: -0.005em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RecentMeta = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 1px;
`;

const RecentStatusPill = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 999px;
  font-size: 11px; font-weight: 500;
  white-space: nowrap;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  border: 1px solid ${(p) => p.$border};
  flex-shrink: 0;
`;

const RecentExpiry = styled.div`
  font-size: 0.75rem;
  color: var(--text-light);
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
`;
const StepEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: #6b4f25;
  background: rgba(184, 149, 106, 0.12);
  padding: 4px 10px;
  border-radius: 999px;
  margin-bottom: 0.875rem;

  @media (max-width: 420px) { font-size: 0.625rem; }
`;

const StepHeading = styled.h2`
  margin: 0 0 0.5rem;
  font-size: clamp(1.375rem, 1.6vw + 0.75rem, 1.625rem);
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 1.2;
  color: var(--text-primary);
`;


const TopBack = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  background: var(--background-primary, #fff);
  border: 1px solid rgba(0, 0, 0, 0.10);
  color: var(--text-primary, #1a202c);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1;
  min-height: 42px;
  padding: 0 14px;
  margin: 0;
  cursor: pointer;
  font-family: inherit;
  border-radius: 8px;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(0, 0, 0, 0.18);
  }

  &:focus-visible {
    outline: 2px solid rgba(184, 149, 106, 0.5);
    outline-offset: 2px;
  }
`;

const StepHint = styled.p`
  margin: 0 0 1.5rem;
  font-size: 0.9375rem;
  color: var(--text-muted);
  line-height: 1.55;
  max-width: 58ch;

  @media (max-width: 600px) { margin-bottom: 1.125rem; }
`;

/* Justification textarea (final step) */
const Label = styled.label`
  display: block; font-size: 11px; font-weight: 500; letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--text-muted); margin-bottom: 6px;
`;
const Textarea = styled.textarea`
  width: 100%; min-height: 130px; padding: 10px 12px;
  border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 8px;
  font-family: inherit; font-size: 14px; line-height: 1.45;
  background: #fff; resize: vertical; box-sizing: border-box;
  &:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(184, 149, 106, 0.14); }

  @media (max-width: 600px) { min-height: 150px; font-size: 16px; }
`;
const Hint = styled.p`
  margin: 6px 0 0; font-size: 12px;
  color: ${(p) => (p.$error ? '#A86A4A' : 'var(--text-light)')};
`;
const ErrMsg = styled.div`
  margin-top: 12px; padding: 10px 12px;
  background: rgba(178, 125, 92, 0.10); border: 1px solid rgba(178, 125, 92, 0.30);
  border-radius: 8px; color: #8C5638; font-size: 13px;
`;
const ReviewCard = styled.div`
  display: flex; gap: 12px; align-items: flex-start;
  padding: 12px 14px; border-radius: 10px;
  background: rgba(0, 0, 0, 0.025); border: 1px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;
const ReviewKey = styled.div`
  font-size: 11px; font-weight: 500; letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--text-muted);
`;
const ReviewVal = styled.div`
  font-size: 0.9375rem; font-weight: 500; color: var(--text-primary);
  margin-top: 2px;
  overflow-wrap: anywhere;
`;

/* Empty / success */
const Empty = styled.div`
  display: flex; flex-direction: column; align-items: center;
  padding: 2.5rem 1rem; color: var(--text-muted); text-align: center; gap: 8px;

  @media (max-width: 420px) { padding: 2rem 0.25rem; }
`;
const EmptyIcon = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 52px; height: 52px; border-radius: 14px;
  background: rgba(184, 149, 106, 0.12); color: #6b4f25; margin-bottom: 4px;
`;
const SuccessBox = styled.div`
  display: flex; gap: 12px; align-items: flex-start;
  padding: 14px 16px; border-radius: 12px;
  background: rgba(107, 142, 127, 0.10); border: 1px solid rgba(107, 142, 127, 0.30);
  color: #466B5E;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

/* -------------------------- Helpers -------------------------- */
function formatDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return String(value); }
}

function formatCourseDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return String(value); }
}

function formatCourseDateRange(course) {
  const start = formatCourseDate(course?.courseStartDate);
  const end = formatCourseDate(course?.courseEndDate);
  if (start && end) return `${start} – ${end}`;
  if (start) return `Starts ${start}`;
  if (end) return `Ends ${end}`;
  return null;
}

function badgeForStatus(s) {
  if (s === 'pending')  return 'Already pending';
  if (s === 'denied')   return 'Previously denied';
  if (s === 'approved') return 'Extension expired';
  return null;
}

/* -------------------------- Component -------------------------- */
export default function ExtensionRequestWizard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('request');
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);            // 'assignment' | 'quiz'
  const [course, setCourse] = useState(null);        // { courseId, courseName, ... items: [...] }
  const [item, setItem] = useState(null);            // { id, title, dueAt, ... }
  const [justification, setJustification] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [error, setError] = useState('');

  const [eligible, setEligible] = useState({ assignment: [], quiz: [] });
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courseQuery, setCourseQuery] = useState('');
  const [itemQuery, setItemQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eligibleRes, mineRes] = await Promise.allSettled([
        apiService.listEligibleExtensionItems(),
        apiService.listMyExtensions(),
      ]);
      if (eligibleRes.status === 'fulfilled' && eligibleRes.value?.success) {
        setEligible(eligibleRes.value.byType || { assignment: [], quiz: [] });
      }
      if (mineRes.status === 'fulfilled' && mineRes.value?.success) {
        setMyRequests(mineRes.value.requests || []);
      }
    } catch (err) {
      logger.error('Failed to load eligible items', { err });
      setEligible({ assignment: [], quiz: [] });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pendingRequests = useMemo(() => myRequests.filter((r) => r.status === 'pending'), [myRequests]);

  const coursesForType = useMemo(() => (type ? (eligible[type] || []) : []), [type, eligible]);
  const filteredCourses = useMemo(() => {
    const q = courseQuery.trim().toLowerCase();
    if (!q) return coursesForType;
    return coursesForType.filter((c) =>
      `${c.courseCode || ''} ${c.courseName || ''}`.toLowerCase().includes(q)
    );
  }, [coursesForType, courseQuery]);

  const filteredItems = useMemo(() => {
    if (!course) return [];
    const q = itemQuery.trim().toLowerCase();
    if (!q) return course.items;
    return course.items.filter((i) => (i.title || '').toLowerCase().includes(q));
  }, [course, itemQuery]);

  const goNext = () => setStep((s) => Math.min(4, s + 1));
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  const reset = () => {
    setStep(1); setType(null); setCourse(null); setItem(null);
    setJustification(''); setError(''); setSubmittedId(null);
  };

  const submit = async () => {
    if (!type || !item) return;
    if (justification.trim().length < MIN_LEN) return;
    setSubmitting(true); setError('');
    try {
      const res = await apiService.requestExtension({
        itemType: type, itemId: item.id, justification: justification.trim(),
      });
      setSubmittedId(res?.requestId || true);
    } catch (err) {
      setError(err?.errorData?.message || err?.message || 'Failed to submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  // After successful submission, refresh eligible list (the just-submitted item
  // moves to "pending" so it will show that badge if the user opens the wizard again).
  useEffect(() => { if (submittedId) load(); }, [submittedId, load]);

  const tooShort = justification.trim().length < MIN_LEN;
  const remaining = Math.max(0, MIN_LEN - justification.trim().length);

  /* -------------------- Render -------------------- */
  const stepDots = [
    { n: 1, label: 'Type'    },
    { n: 2, label: 'Course'  },
    { n: 3, label: 'Item'    },
    { n: 4, label: 'Submit'  },
  ];

  const stepEyebrowText = (s) => {
    if (s === 1) return 'Step 1 of 4';
    if (s === 2) return 'Step 2 of 4';
    if (s === 3) return 'Step 3 of 4';
    if (s === 4) return 'Step 4 of 4';
    return '';
  };

  const renderPendingRequests = () => (
    <PanelCard>
      <StepEyebrow>Pending Requests</StepEyebrow>
      <StepHeading>Requests awaiting review</StepHeading>
      <StepHint>Your instructor or an administrator will review these extension requests.</StepHint>
      {pendingRequests.length === 0 ? (
        <Empty>
          <EmptyIcon><FiInbox size={22} /></EmptyIcon>
          <div style={{ fontWeight: 600 }}>No pending requests</div>
          <div style={{ fontSize: 13, maxWidth: 44 + 'ch' }}>
            Any extension request awaiting review will appear here.
          </div>
        </Empty>
      ) : (
        <RecentGrid>
          {pendingRequests.map((r) => (
            <RecentCard key={r.id} $accent="#ea580c">
              <RecentTopRow>
                <RecentBody>
                  <RecentItemTitle>{r.item_title || `${r.item_type === 'quiz' ? 'Quiz' : 'Assignment'} #${r.item_id}`}</RecentItemTitle>
                  <RecentMeta>{r.course_code ? `${r.course_code} · ` : ''}{r.course_name}</RecentMeta>
                </RecentBody>
                <RecentStatusPill $bg="rgba(234, 88, 12, 0.10)" $color="#ea580c" $border="rgba(234, 88, 12, 0.30)">
                  <FiClock size={11} /> Pending review
                </RecentStatusPill>
              </RecentTopRow>
              <RecentExpiry>Requested {formatDate(r.requested_at)}</RecentExpiry>
            </RecentCard>
          ))}
        </RecentGrid>
      )}
    </PanelCard>
  );

  return (
    <Page>
      <PageOrb aria-hidden="true" />
      <PageInner>
      <Hero>
        <HeroEyebrow>{new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</HeroEyebrow>
        <HeroTitleWrap><HeroTitle>Request an Extension</HeroTitle></HeroTitleWrap>
        <HeroSub>
          Select the item you would like to submit after its due date. Your request
          will be reviewed by your instructor or an administrator.
        </HeroSub>
      </Hero>

      <ContentShell>
        <SectionNav aria-label="Extension request sections">
          <SectionNavItem type="button" onClick={() => setActiveTab('request')} $active={activeTab === 'request'}>
            <FiFileText size={16} /> Request Extension
          </SectionNavItem>
          <SectionNavItem type="button" onClick={() => setActiveTab('pending')} $active={activeTab === 'pending'}>
            <FiClock size={16} /> Pending Requests
          </SectionNavItem>
        </SectionNav>

        <MainStack>
      {activeTab === 'pending' ? renderPendingRequests() : (
        <PanelCard>
          {!submittedId && (
            <>
              <StepsTrack $count={stepDots.length} aria-label="Wizard progress">
                {stepDots.map((s, i) => (
                  <StepCol key={s.n}>
                    {i > 0 && <StepLine $position="left" $done={step > s.n - 1} />}
                    {i < stepDots.length - 1 && <StepLine $position="right" $done={step > s.n} />}
                    <StepNode $active={step === s.n} $done={step > s.n}>
                      {step > s.n ? <FiCheckCircle size={15} /> : s.n}
                    </StepNode>
                    <StepLabel $active={step === s.n} $done={step > s.n}><DesktopLabel>{s.label}</DesktopLabel></StepLabel>
                  </StepCol>
                ))}
              </StepsTrack>
              <MobileStepText>{stepEyebrowText(step)} · {stepDots.find((s) => s.n === step)?.label}</MobileStepText>
            </>
          )}

      {/* ============ Success state ============ */}
      {submittedId ? (
        <StepBody>
          <SuccessBox>
            <FiCheckCircle size={20} />
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                Request submitted
              </div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                Your extension request for “{item?.title}” has been submitted and is pending review.
                You'll see updates on your dashboard and on the {type === 'quiz' ? 'My Quizzes & Exams' : 'My Assignments'} page.
              </div>
            </div>
          </SuccessBox>
          <Footer>
            <BackBtn onClick={() => navigate(type === 'quiz' ? '/student/quizzes' : '/assignments')}>
              Back to {type === 'quiz' ? 'My Quizzes' : 'My Assignments'}
            </BackBtn>
            <NextBtn onClick={reset}>Request another <FiArrowRight size={13} /></NextBtn>
          </Footer>
        </StepBody>
      ) : (
        <>
          {/* ============ STEP 1: Type ============ */}
          {step === 1 && (
            <StepBody initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24 }}>
              <StepEyebrow>{stepEyebrowText(1)}</StepEyebrow>
              <StepHeading>What are you requesting an extension for?</StepHeading>
              <StepHint>Pick the type of work you need access to. We'll narrow down the options on the next few steps.</StepHint>
              <TileGrid $cols={2}>
                <Tile onClick={() => { setType('assignment'); setCourse(null); setItem(null); goNext(); }} $selected={type === 'assignment'}>
                  <TileIcon $tint="rgba(184, 149, 106, 0.12)" $color="#6b4f25"><FiFileText size={18} /></TileIcon>
                  <TileTitle>Assignment</TileTitle>
                  <TileSub>Reflection essays, weekly responses, projects, and other written submissions.</TileSub>
                  <TileBadge>{(eligible.assignment || []).reduce((n, c) => n + c.items.length, 0)} available</TileBadge>
                </Tile>
                <Tile onClick={() => { setType('quiz'); setCourse(null); setItem(null); goNext(); }} $selected={type === 'quiz'}>
                  <TileIcon $tint="rgba(79, 130, 144, 0.12)" $color="#2F6470"><TbChecklist size={18} /></TileIcon>
                  <TileTitle>Quiz or Exam</TileTitle>
                  <TileSub>Homework quizzes, midterms, and final exams.</TileSub>
                  <TileBadge>{(eligible.quiz || []).reduce((n, c) => n + c.items.length, 0)} available</TileBadge>
                </Tile>
              </TileGrid>
            </StepBody>
          )}

          {/* ============ STEP 2: Course ============ */}
          {step === 2 && (
            <StepBody initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24 }}>
              <StepTopRow>
                <StepEyebrow>{stepEyebrowText(2)}</StepEyebrow>
                <TopBack type="button" onClick={goBack}><FiArrowLeft size={13} /> Back</TopBack>
              </StepTopRow>
              <StepHeading>Which course?</StepHeading>
              <StepHint>
                Showing courses with at least one {type === 'quiz' ? 'quiz or exam' : 'assignment'} you can request access to.
              </StepHint>

              {coursesForType.length > 6 && (
                <SearchWrap>
                  <SearchIcon><FiSearch size={14} /></SearchIcon>
                  <SearchInput
                    placeholder="Search by code or name…"
                    value={courseQuery}
                    onChange={(e) => setCourseQuery(e.target.value)}
                  />
                </SearchWrap>
              )}

              {loading ? (
                <Empty><EmptyIcon><FiClock size={22} /></EmptyIcon> Loading…</Empty>
              ) : coursesForType.length === 0 ? (
                <Empty>
                  <EmptyIcon><FiInbox size={22} /></EmptyIcon>
                  <div style={{ fontWeight: 600 }}>Nothing here right now</div>
                  <div style={{ fontSize: 13, maxWidth: 44 + 'ch' }}>
                    Every {type === 'quiz' ? 'quiz' : 'assignment'} that would qualify is currently accessible (active course, active extension, or already completed).
                  </div>
                </Empty>
              ) : (
                <>
                  {filteredCourses.some((c) => !c.courseActive) && (
                    <>
                      <SectionRow><SectionLabel>Past Courses</SectionLabel></SectionRow>
                      <TileGrid $cols={2}>
                        {filteredCourses.filter((c) => !c.courseActive).map((c) => {
                          const dateRange = formatCourseDateRange(c);
                          return (
                            <Tile key={c.courseId} onClick={() => { setCourse(c); setItem(null); goNext(); }} $selected={course?.courseId === c.courseId}>
                              <TileIcon $tint="rgba(0,0,0,0.04)" $color="#525252"><TbCalendarOff size={18} /></TileIcon>
                              <TileTitle>{c.courseName}</TileTitle>
                              <TileSub>{c.courseCode || `Course ${c.courseId}`}</TileSub>
                              {c.instructorName && (
                                <TileInstructor>
                                  <FiUser size={12} />
                                  {c.instructorName}
                                </TileInstructor>
                              )}
                              {dateRange && <TileDateLine>{dateRange}</TileDateLine>}
                              <TileBadge>{c.items.length} {c.items.length === 1 ? 'item' : 'items'}</TileBadge>
                            </Tile>
                          );
                        })}
                      </TileGrid>
                    </>
                  )}
                  {filteredCourses.some((c) => c.courseActive) && (
                    <>
                      <SectionRow><SectionLabel>Active Courses (locked exams)</SectionLabel></SectionRow>
                      <TileGrid $cols={2}>
                        {filteredCourses.filter((c) => c.courseActive).map((c) => {
                          const dateRange = formatCourseDateRange(c);
                          return (
                            <Tile key={c.courseId} onClick={() => { setCourse(c); setItem(null); goNext(); }} $selected={course?.courseId === c.courseId}>
                              <TileIcon $tint="rgba(0,0,0,0.04)" $color="#525252"><TbCalendarOff size={18} /></TileIcon>
                              <TileTitle>{c.courseName}</TileTitle>
                              <TileSub>{c.courseCode || `Course ${c.courseId}`}</TileSub>
                              {c.instructorName && (
                                <TileInstructor>
                                  <FiUser size={12} />
                                  {c.instructorName}
                                </TileInstructor>
                              )}
                              {dateRange && <TileDateLine>{dateRange}</TileDateLine>}
                              <TileBadge>{c.items.length} {c.items.length === 1 ? 'exam' : 'exams'}</TileBadge>
                            </Tile>
                          );
                        })}
                      </TileGrid>
                    </>
                  )}
                </>
              )}

            </StepBody>
          )}

          {/* ============ STEP 3: Item ============ */}
          {step === 3 && course && (
            <StepBody initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24 }}>
              <StepTopRow>
                <StepEyebrow>{stepEyebrowText(3)}</StepEyebrow>
                <TopBack type="button" onClick={goBack}><FiArrowLeft size={13} /> Back</TopBack>
              </StepTopRow>
              <StepHeading>Which {type === 'quiz' ? 'quiz or exam' : 'assignment'}?</StepHeading>
              <StepHint>
                {course.courseCode ? `${course.courseCode} · ` : ''}{course.courseName}
                {course.instructorName ? ` · ${course.instructorName}` : ''}
              </StepHint>

              {course.items.length > 6 && (
                <SearchWrap>
                  <SearchIcon><FiSearch size={14} /></SearchIcon>
                  <SearchInput
                    placeholder="Search by title…"
                    value={itemQuery}
                    onChange={(e) => setItemQuery(e.target.value)}
                  />
                </SearchWrap>
              )}

              <TileGrid $cols={1}>
                {filteredItems.map((i) => {
                  const badge = badgeForStatus(i.lastRequestStatus);
                  const disabled = i.lastRequestStatus === 'pending';
                  return (
                    <Tile
                      key={i.id}
                      onClick={() => { if (!disabled) { setItem(i); goNext(); } }}
                      $selected={item?.id === i.id}
                      disabled={disabled}
                    >
                      <TileIcon $tint="rgba(184, 149, 106, 0.10)" $color="#6b4f25">
                        {type === 'quiz' ? <TbChecklist size={18} /> : <FiFileText size={18} />}
                      </TileIcon>
                      <TileTitle>{i.title}</TileTitle>
                      <TileSub>
                        {i.dueAt ? `Due ${formatDate(i.dueAt)}` : 'No due date'}
                        {i.quizType ? ` · ${i.quizType === 'final_exam' ? 'Final Exam' : i.quizType === 'midterm' ? 'Midterm' : 'Homework'}` : ''}
                      </TileSub>
                      {badge && <TileBadge>{badge}</TileBadge>}
                    </Tile>
                  );
                })}
              </TileGrid>

            </StepBody>
          )}

          {/* ============ STEP 4: Justify + submit ============ */}
          {step === 4 && item && (
            <StepBody initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24 }}>
              <StepTopRow>
                <StepEyebrow>{stepEyebrowText(4)}</StepEyebrow>
                <TopBack type="button" onClick={goBack}><FiArrowLeft size={13} /> Back</TopBack>
              </StepTopRow>
              <StepHeading>One last step — explain why</StepHeading>
              <StepHint>Please share a brief reason why you need additional time to complete this task.</StepHint>

              <ReviewCard>
                <TileIcon $tint="rgba(184, 149, 106, 0.10)" $color="#6b4f25">
                  {type === 'quiz' ? <TbChecklist size={18} /> : <FiFileText size={18} />}
                </TileIcon>
                <div>
                  <ReviewKey>Course</ReviewKey>
                  <ReviewVal>{course?.courseCode ? `${course.courseCode} · ` : ''}{course?.courseName}</ReviewVal>
                  {course?.instructorName && (
                    <>
                      <ReviewKey style={{ marginTop: 8 }}>Instructor</ReviewKey>
                      <ReviewVal>{course.instructorName}</ReviewVal>
                    </>
                  )}
                  <ReviewKey style={{ marginTop: 8 }}>{type === 'quiz' ? 'Quiz' : 'Assignment'}</ReviewKey>
                  <ReviewVal>{item.title}</ReviewVal>
                  {item.dueAt && (
                    <>
                      <ReviewKey style={{ marginTop: 8 }}>Originally due</ReviewKey>
                      <ReviewVal>{formatDate(item.dueAt)}</ReviewVal>
                    </>
                  )}
                </div>
              </ReviewCard>

              <Label htmlFor="wizard-justification">Justification</Label>
              <Textarea
                id="wizard-justification"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                maxLength={2000}
                autoFocus
              />
              <Hint $error={tooShort && justification.trim().length > 0}>
                {tooShort
                  ? `${remaining} more character${remaining === 1 ? '' : 's'} required (minimum ${MIN_LEN}).`
                  : `${justification.trim().length} characters.`}
              </Hint>
              {error && <ErrMsg><FiAlertCircle size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />{error}</ErrMsg>}

              <Footer>
                <FooterSpacer />
                <NextBtn onClick={submit} disabled={tooShort || submitting}>
                  {submitting ? 'Submitting…' : 'Submit Request'}
                </NextBtn>
              </Footer>
            </StepBody>
          )}
        </>
      )}
        </PanelCard>
      )}
        </MainStack>
      </ContentShell>
      </PageInner>
    </Page>
  );
}
